<template>
    <v-dialog
        v-model="modalOpen"
        width=75%
        outline
    >
        <template v-slot:activator="{ on }">
          <v-btn
            data-testid="edit-alert-btn"
            class="edit-alert-btn ml-0 mr-2"
            small
            v-on="on"
            color="primary"
          >
            Edit
          </v-btn>
        </template>

        <v-card>
            <v-card-title class="text-h5" primary-title>
                <span>Edit Alert</span>
            </v-card-title>
            <v-divider />
            <v-card-text>
                <v-layout>
                    <v-text-field
                      :data-testid="`alert-id-${alert.id}`"
                      :class="`alert-id-${alert.id}`"
                      class="pr-1"
                      :value="alert.id"
                      filled
                      disabled
                      label="Alert ID"
                    />
                    <facilities-select
                      :alert.sync="alert"
                    />
                </v-layout>

                <v-textarea
                  data-testid="edit-alert-textarea"
                  class="edit-alert-textarea"
                  v-model="alert.text"
                  filled
                  placeholder="Enter alert..."
                  label="Alert text"
                  aria-required
                />

                <v-menu
                  ref="editAlertDateMenu"
                  v-model="editAlertDateMenu"
                  :close-on-content-click="false"
                  :return-value.sync="alert.expires_at"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="alert.expires_at"
                      label="Select expiration or leave empty to show indefinitely."
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="alert.expires_at"
                    no-title
                    scrollable
                    :min="tomorrow"
                  >
                    <v-spacer></v-spacer>
                    <v-btn
                      text
                      color="primary"
                      @click="editAlertDateMenu = false"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      text
                      color="primary"
                      @click="$refs.editAlertDateMenu.save(alert.expires_at)"
                    >
                      OK
                    </v-btn>
                  </v-date-picker>
                </v-menu>

                <v-select
                  :data-testid="`edit-alert-${alert.id}-color-select`"
                  :class="`edit-alert-${alert.id}-color-select`"
                  v-model="updatedColor"
                  filled
                  :items="colors"
                  name="color"
                  label="Select Alert Color"
                  item-text="color"
                  item-value="value"
                  return-object
                />

                <div class="mt-1 mb-3 px-1 warning--text text-body-2">
                    This alert will display
                    {{ whereWillAlertDisplay }}
                    <span v-if="alert.expires_at"> until {{ formatDate(alert.expires_at) }}</span>
                    <span v-else> indefinitely</span>.
                </div>

                <div class="px-1 subheading info--text">Last updated {{ timeStamp(alert.updated_at) }}</div>
            </v-card-text>
            <v-divider />
            <v-card-actions>
                <v-btn
                  data-testid="edit-alert-cancel-btn"
                  class="edit-alert-cancel-btn"
                  color="error"
                  @click="modalOpen = false"
                >
                  Cancel
                </v-btn>

                <v-spacer />
                <v-btn
                  data-testid="edit-alert-save-btn"
                  class="edit-alert-save-btn"
                  color="primary"
                  :loading="loading"
                  :disabled="!canSubmit"
                  @click="submitEdit"
                >
                  Save
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import FacilitiesSelect from "@/components/FacilitiesSelect";
import moment from "moment";

export default {
  props: ["alert", "colors"],

  data() {
    return {
      modalOpen: false,
      loading: false,
      selectedColor: "Red",
      editAlertDateMenu: false
    };
  },

  methods: {
    clearFields() {
      this.alert.facilityId = "";
      this.alert.text = "";
      this.alert.facility_id = null;
      this.alert.selectedColor = undefined;
    },

    submitEdit() {
      this.loading = true;
      this.$store.dispatch("updateAlert", this.alertData)
        .then(() => {
          this.loading = false;
          this.modalOpen = false;
          this.$store.dispatch("getAllAlerts");
        });
    },

    checkGlobal(id) {
      return !id ? "Global" : id;
    },

    formatDate(date) {
      return date ? moment(date).local().add(24, "hours").format("MM-DD-YYYY, hh:mm a") : "No expiration";
    },

    timeStamp(date) {
      return moment.utc(date).local().fromNow();
    }
  },

  computed: {
    facility() {
      const facilities = this.$store.state.facilities.allFacilities;
      const facilityId = this.alert.facility_id;
      const facility = facilities.find(obj => obj.id === facilityId);
      return facility;
    },
    whereWillAlertDisplay() {
      if (this.facility && this.facility.location_code) {
        return ` for facility ${this.facility.location_code} `;
      }
      return "globally";
    },
    alertData() {
      return {
        facility_id: this.alert.facility_id | null,
        text: this.alert.text,
        expires_at: this.alert.expires_at ? moment(this.alert.expires_at).local() : null,
        alert_id: this.alert.id | null,
        color: this.alert.color
      };
    },

    canSubmit() {
      let now = moment();
      let before = moment(this.alert.expires_at).isBefore(now);
      return !!(this.alert.text && !before);
    },

    // @todo this is a mess and should probably be cleaned up
    updatedColor: {
      get() {
        if (this.alert.color === "info") {
          return "Blue";
        } else if (this.alert.color === "success") {
          return "Green";
        } else if (this.alert.color === "error") {
          return "Red";
        } else if (this.alert.color === "warning" || !this.alert.color) {
          return "Orange";
        }

        return "Orange";
      },

      set(value) {
        switch (value) {
          case "Blue":
            this.alert.color = "info";
            break;
          case "Green":
            this.alert.color = "success";
            break;
          case "Red":
            this.alert.color = "error";
            break;
          case "Orange":
            this.alert.color = "warning";
            break;
          default:
            this.alert.color = "warning";
            break;
        }
      }
    },

    tomorrow() {
      return new moment().add(1, "days").format("YYYY-MM-DD");
    }
  },

  components: {
    FacilitiesSelect
  }
};
</script>
